import { __assign, __spreadArrays } from "tslib";
import * as React from 'react';
import { namespacedClassnames, noop } from '../../../common';
import { ScrollableBar, Select, Row, ResponsiveBreadcrumbs, Tooltip, NeutralMessageBox } from '../../../primitives';
import { Text, Button, RISK_BAD_UX_DISABLE_TOOLTIP, LibColor } from '../../../new-libby';
import { useDebounce } from '@wix/answers-lib';
import { menuHeaderStyle } from '../../selectors/select-v3-styles';
import { ReactSelect3Components } from '../../../common/react-select';
import { chevronIcon, filterResults, getDeepPath, noOptions, noResults, searchDebounceTime } from './../drill-selectors-common';
import { compKey } from './key';
export var translationPrefix = "common.libby.drill-single-select";
export var DrillSingleSelect = function (props) {
    var _a = React.useState(props.options), currentOptions = _a[0], setCurrentOptions = _a[1];
    var _b = React.useState([]), searchResults = _b[0], setSearchResults = _b[1];
    var _c = React.useState(), breadCrumbsItems = _c[0], setBreadcrumbsItems = _c[1];
    var _d = React.useState(''), query = _d[0], setQuery = _d[1];
    var _e = React.useState(false), menuOpen = _e[0], setMenuOpen = _e[1];
    var debounceSearch = useDebounce(function (text) { return setQuery(text); }, searchDebounceTime);
    var rootLabel = props.rootLabel, options = props.options, className = props.className, limitResults = props.limitResults, texts = props.texts;
    var maybeHasResultsFilter = !!query.length && !!limitResults && searchResults.length > limitResults;
    var noQueryHeader = (React.createElement(ScrollableBar, null,
        React.createElement(ResponsiveBreadcrumbs, { hideTooltip: true, noItemsShrinkage: true, items: breadCrumbsItems || [] })));
    var rootCategory = function (disableClick) {
        var onClick = (function (_idx, e) {
            e === null || e === void 0 ? void 0 : e.preventDefault();
            e === null || e === void 0 ? void 0 : e.stopPropagation();
            setCurrentOptions(options);
        });
        return {
            label: rootLabel,
            onClick: !disableClick ? onClick : undefined
        };
    };
    var findMatchingOption = function (find) {
        var matchingOptions = options;
        var findMatchingOptionInner = function (items) {
            items.forEach(function (item) {
                var _a;
                if (item.children && ((_a = item.children) === null || _a === void 0 ? void 0 : _a.map(function (v) { return v.value; }).includes(find.value))) {
                    matchingOptions = item.children;
                }
                else if (item.children) {
                    findMatchingOptionInner(item.children);
                }
            });
        };
        findMatchingOptionInner(options);
        return matchingOptions;
    };
    var Menu = function (menuProps) {
        var withQuery = (React.createElement("div", { className: 'select-menu-header search-results-header', style: menuHeaderStyle }, searchResults.length + " " + texts.results));
        var menuHeader = (React.createElement("div", { className: 'select-menu-header', style: menuHeaderStyle }, noQueryHeader));
        var searchMoreThanLmt = (React.createElement("div", { className: 'results-limit-notification' },
            React.createElement(NeutralMessageBox, { icon: React.createElement(React.Fragment, null) }, texts.resultsLimit(searchResults.length - (limitResults || 0)))));
        return (React.createElement(ReactSelect3Components.Menu, __assign({}, menuProps),
            !!query.length ? withQuery : menuHeader,
            menuProps.children,
            maybeHasResultsFilter ? searchMoreThanLmt : null));
    };
    var valueRenderer = function (option) {
        if (typeof option.label !== 'string') {
            return React.createElement(React.Fragment, null, option.label);
        }
        else {
            return optionAsBreadcrumb(option);
        }
    };
    var optionAsBreadcrumb = function (option, hideTooltip) { return (React.createElement(ResponsiveBreadcrumbs, { key: option.value, hideTooltip: !!hideTooltip, items: __spreadArrays(createBreadcrumbs(true, [option]), [option]) })); };
    React.useEffect(function () {
        var findPath = createBreadcrumbs(false, currentOptions);
        setBreadcrumbsItems(findPath);
    }, [options, currentOptions]);
    React.useEffect(function () {
        setQuery('');
        var findPath = createBreadcrumbs(false, props.value ? [props.value] : currentOptions);
        setBreadcrumbsItems(findPath);
        var matchingOptions = props.value ? findMatchingOption(props.value) : props.options;
        setCurrentOptions(matchingOptions);
    }, [props.value]);
    React.useEffect(function () {
        if (!!query.length) {
            setSearchResults(renderResults(query));
        }
    }, [query]);
    var createBreadcrumbItemFromOption = function (item, lastItem) {
        var click = function (_idx, e) {
            e === null || e === void 0 ? void 0 : e.preventDefault();
            e === null || e === void 0 ? void 0 : e.stopPropagation();
            setCurrentOptions(item.children ? item.children : options);
        };
        return { label: item.label, onClick: !lastItem ? click : undefined };
    };
    var createBreadcrumbs = function (disableClick, find) {
        if (find === void 0) { find = currentOptions; }
        var path = getDeepPath(find, options);
        var pathAsBc = path.map(function (item, idx) {
            return createBreadcrumbItemFromOption(item, disableClick || idx === path.length - 1);
        });
        return __spreadArrays([rootCategory(path.length === 0 || disableClick)], pathAsBc);
    };
    var classNames = namespacedClassnames(compKey, className);
    var renderResults = function (label) {
        var filteredResults = filterResults(label, options);
        return filteredResults.map(function (item) { return (__assign(__assign({}, item), { value: item.value, label: optionAsBreadcrumb(item) })); });
    };
    var drillInto = function (e, option) {
        e === null || e === void 0 ? void 0 : e.preventDefault();
        e === null || e === void 0 ? void 0 : e.stopPropagation();
        if (!option.drillAndSelectDisabled && !option.drillDisabled) {
            setCurrentOptions(option.children ? option.children : options);
            setQuery('');
        }
    };
    var onMenuClose = function () {
        var matchingOptions = props.value ? findMatchingOption(props.value) : options;
        setCurrentOptions(matchingOptions);
        setMenuOpen(false);
    };
    var onSearch = function (text) {
        debounceSearch(text);
    };
    var onMenuOpen = function () {
        setMenuOpen(true);
    };
    var optionRenderer = function (option) {
        var _a;
        var isSelected = ((_a = props.value) === null || _a === void 0 ? void 0 : _a.value) === option.value;
        var label = option.label, children = option.children, drillDisabled = option.drillDisabled, drillAndSelectDisabled = option.drillAndSelectDisabled, selectDisabled = option.selectDisabled;
        var hasChildren = children && children.length > 0;
        var drillClick = function (e) { return drillInto(e, option); };
        var buttonsColor = drillDisabled || drillAndSelectDisabled ?
            LibColor.grey100 : !isSelected ? LibColor.blue100 : LibColor.white;
        var labelColors = drillAndSelectDisabled ? 't50' : !isSelected ? 't20' : 't01';
        var selectColors = drillAndSelectDisabled || selectDisabled ? 't50' : !isSelected ? 't60' : 't01';
        var disableSelect = selectDisabled || (drillAndSelectDisabled && RISK_BAD_UX_DISABLE_TOOLTIP);
        var optionRendererClasses = namespacedClassnames('drill-select-item', { selected: isSelected });
        var drillSelectWrapperClasses = namespacedClassnames('drill-select-wrapper', { isSelected: isSelected });
        return (React.createElement(Tooltip, { key: option.value, direction: 'right', className: drillSelectWrapperClasses, forceHidden: !drillAndSelectDisabled, relativeToBody: true, body: drillAndSelectDisabled || '' },
            React.createElement("div", { className: 'drill-select-mouse-down-wrapper' },
                React.createElement(Row, { horizontalAlignment: 'space-between', verticalAlignment: 'center', className: optionRendererClasses },
                    React.createElement(Row, { className: 'label-wrapper', horizontalAlignment: 'space-between', verticalAlignment: 'center' },
                        React.createElement(Text, { color: labelColors, className: 'label-text' }, label)),
                    React.createElement(Row, { verticalAlignment: 'center', horizontalAlignment: 'center', className: 'buttons-wrapper' },
                        React.createElement(Button, { onClick: noop, disabled: disableSelect, className: 'select-button', type: 'tertiary' },
                            React.createElement(Text, { color: selectColors, className: 'select-button-text' }, texts.select)),
                        hasChildren && chevronIcon(drillClick, buttonsColor, drillDisabled || (drillAndSelectDisabled ? RISK_BAD_UX_DISABLE_TOOLTIP : '')))))));
    };
    var selectOptions = query.length > 0 ? searchResults.slice(0, props.limitResults) : currentOptions;
    var parseOptions = selectOptions.map(function (option) { return (__assign(__assign({}, option), { isDisabled: !!option.drillAndSelectDisabled || !!option.selectDisabled })); });
    var value = menuOpen ? null : props.value;
    return (React.createElement("div", { className: classNames },
        React.createElement(Select, __assign({}, props, { valueRenderer: valueRenderer, options: parseOptions, components: { Menu: Menu }, optionRenderer: optionRenderer, value: value, onClose: onMenuClose, disableOptionFiltering: true, onOpen: onMenuOpen, noOptionsMessage: props.noOptionsMessage || texts.noOptions && noOptions(texts.noOptions), noResultsText: props.noResultsText || texts.noResults && noResults(texts.noResults, query), onInputChange: props.onInputChange || onSearch }))));
};
//# sourceMappingURL=index.js.map